import { Access } from "@p0security/shared/types/access/index";

import { parseClusterId } from "./components.ts";

/** What both accesses stage. Revoke reads these, not the cluster item, so it
 * undoes what this grant did even when the item changed since. */
const generated = {
  roleName: {
    description:
      "The IAM role P0 created for this grant alone, on path /p0/redshift-ai/",
    label: "IAM role",
    type: "string",
  },
  arn: {
    description: "The IAM role's ARN, which the agent's MCP server assumes",
    label: "IAM role ARN",
    type: "string",
  },
  user: {
    description:
      "The database user the IAM role signs in as, IAMR:<IAM role name>",
    label: "Redshift user",
    type: "string",
  },
  privilegeRole: {
    description:
      "The Redshift role that holds the granted object privileges. Only object access has one.",
    label: "Privilege role",
    optional: true,
    type: "string",
  },
  targetArn: {
    description: "The ARN of the cluster or workgroup P0 granted on",
    label: "Cluster ARN",
    type: "string",
  },
} as const;

const cluster = {
  type: "dynamic",
  label: "Cluster",
  description:
    "Redshift provisioned cluster or Serverless workgroup, as installed in P0",
} as const;

/** What the agent reads once access is granted: where, as whom, and which
 * MCP server reaches it. The server's label is `Amazon Redshift (<name>)`. */
// Typed by hand: inferring it from the access would be circular.
const instructions = ({
  generated,
  permission,
}: {
  generated: { user?: string };
  permission: { cluster?: string };
}) => {
  const cluster = permission.cluster ?? "";
  const name = parseClusterId(cluster)?.name ?? cluster;
  const user = generated.user;
  return `Use the Amazon Redshift (${name}) MCP server. It signs in to ${name} as ${user ?? "this grant's own database user"}, which holds what this request granted.`;
};

export const RedshiftAiAccesses = {
  role: Access({
    description:
      "Existing Redshift roles, granted to an agent's own short-lived database user",
    label: "Role",
    maturity: "beta",
    delegation: {},
    generated,
    parameters: {
      cluster,
      name: {
        type: "dynamic",
        label: "Roles",
        description: "Existing Redshift roles to grant",
        multivalued: true,
      },
    },
    render: {
      instructions,
      locator: "cluster",
      policy: "name",
    } as const,
  }),
  objects: Access({
    description:
      "Privileges on Redshift databases, schemas, tables or views, granted to an agent's own short-lived database user",
    label: "Object privileges",
    maturity: "beta",
    delegation: {},
    generated,
    parameters: {
      cluster,
      // Strings rather than objects, so every request surface (CLI, Slack,
      // Teams, web) can enter them.
      grants: {
        type: "dynamic",
        label: "Grants",
        description:
          "PRIVILEGE:OBJECT entries, where PRIVILEGE is SELECT, INSERT, UPDATE or DELETE, and OBJECT is DB, DB.SCHEMA or DB.SCHEMA.TABLE",
        multivalued: true,
      },
    },
    render: {
      instructions,
      locator: "cluster",
      policy: "grants",
    } as const,
  }),
};

export type RedshiftAiAccesses = typeof RedshiftAiAccesses;
export type RedshiftAiAccess = RedshiftAiAccesses[keyof RedshiftAiAccesses];
export type RedshiftAiRoleAccess = RedshiftAiAccesses["role"];
export type RedshiftAiObjectsAccess = RedshiftAiAccesses["objects"];
